import { AsyncPipe, NgClass, NgTemplateOutlet } from '@angular/common';
import { HttpErrorResponse } from '@angular/common/http';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  inject,
} from '@angular/core';
import {
  FormControl,
  FormGroup,
  ReactiveFormsModule,
  Validators,
} from '@angular/forms';
import { TranslocoDirective } from '@jsverse/transloco';
import { BehaviorSubject, Subscription } from 'rxjs';
import { CountryService } from 'src/core/services/country.service';
import { AuthStore } from 'src/core/stores';
import {
  CoreUserProfileService,
  UpdateUserProfileDto,
  UserProfileDto,
} from 'src/generated/api/nestjs-backend';
import { passwordsMatchValidator } from '../../../auth/validators/passwords-match.validator';
import { AvatarComponent } from '../../avatar/avatar.component';

@Component({
  selector: 'core-edit-profile',
  templateUrl: './edit-profile.component.html',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    ReactiveFormsModule,
    TranslocoDirective,
    NgClass,
    AsyncPipe,
    AvatarComponent,
    NgTemplateOutlet,
  ],
})
export class EditProfileComponent implements OnInit, OnDestroy {
  private elementRef = inject(ElementRef);
  private authStore = inject(AuthStore);
  private coreUserProfileService = inject(CoreUserProfileService);
  private cdr = inject(ChangeDetectorRef);
  countryService = inject(CountryService);

  private static readonly ALERT_DISPLAY_TIME = 3000;

  showPassword = false;
  showAlert = false;
  user: Partial<UserProfileDto>;
  submitted = false;
  originalUser!: Partial<UserProfileDto>;
  updateSuccess$$ = new BehaviorSubject<boolean>(false);
  emailTakenError$$ = new BehaviorSubject(false);
  genericError$$ = new BehaviorSubject(false);
  titles: string[] = ['Mr.', 'Ms.', 'Mrs.', 'Dr.'];
  selectedTitle: string | null = null;
  userForm: FormGroup;

  constructor() {
    this.user = this.authStore.currentUser()!;
    this.userForm = this.initFormValidation(this.user);
  }

  ngOnInit() {
    this.coreUserProfileService
      .getUserProfile({
        xTenantSlug: this.authStore.tenantSlug(),
      })
      .subscribe((userProfile) => {
        this.user = userProfile;
        this.originalUser = { ...this.user };
        this.userForm = this.initFormValidation(userProfile);
      });
  }

  get userFormFirstName() {
    return this.userForm.get('userFormFirstName');
  }

  get userFormLastName() {
    return this.userForm.get('userFormLastName');
  }

  get userFormEmail() {
    return this.userForm.get('userFormEmail');
  }

  get userFormCompanyName() {
    return this.userForm.get('userFormCompanyName');
  }

  get userFormPassword() {
    return this.userForm.get('userFormPassword');
  }

  get userFormConfirmPassword() {
    return this.userForm.get('userFormConfirmPassword');
  }

  get userFormBusinessUnit() {
    return this.userForm.get('userFormBusinessUnit');
  }

  get userFormTitle() {
    return this.userForm.get('userFormTitle');
  }

  get userFormCountry() {
    return this.userForm.get('userFormCountry');
  }

  selectTitle(event: Event): void {
    this.selectedTitle = (event.target as HTMLInputElement).value;
  }

  getInputType(): string {
    return this.showPassword ? 'text' : 'password';
  }

  toggleShowPassword(): void {
    this.showPassword = !this.showPassword;
  }

  ngOnDestroy() {
    this.elementRef.nativeElement.remove();
  }

  updateProfile() {
    if (this.user.firstName && this.user.email) {
      this.updateUser();
    }
  }

  private initFormValidation(user: Partial<UserProfileDto>): FormGroup {
    return new FormGroup(
      {
        userFormTitle: new FormControl(user?.title || ''),
        userFormFirstName: new FormControl(user?.firstName || '', {
          validators: [
            Validators.required,
            Validators.minLength(2),
            Validators.maxLength(64),
          ],
        }),
        userFormLastName: new FormControl(user?.lastName || '', {
          validators: [
            Validators.required,
            Validators.minLength(2),
            Validators.maxLength(64),
          ],
        }),
        userFormEmail: new FormControl(user?.email || '', {
          validators: [Validators.required, Validators.email],
        }),
        userFormCompanyName: new FormControl(user?.companyName || '', {
          validators: [Validators.required],
        }),
        userFormBusinessUnit: new FormControl(user?.businessUnit || '', {
          updateOn: 'blur',
        }),
        userFormCountry: new FormControl(user?.country || '', {
          validators: [Validators.required],
        }),
        userFormPassword: new FormControl('', {
          validators: [
            Validators.pattern(
              /^(?=.*[0-9])(?=.*[a-z])(?=.*[A-Z])(?=.*\W)(?!.* ).{12,128}$/,
            ),
          ],
        }),
        userFormConfirmPassword: new FormControl('', {
          validators: [
            Validators.pattern(
              /^(?=.*[0-9])(?=.*[a-z])(?=.*[A-Z])(?=.*\W)(?!.* ).{12,128}$/,
            ),
          ],
        }),
      },
      {
        validators: passwordsMatchValidator(
          'userFormPassword',
          'userFormConfirmPassword',
        ),
      },
    );
  }

  private updateUser(): Subscription {
    this.userForm.disable();
    this.authStore.setIsLoading(true);
    const updatedUser: UpdateUserProfileDto = this.createUpdatedUserDto();

    return this.coreUserProfileService
      .updateUserProfile({
        xTenantSlug: this.authStore.tenantSlug(),
        updateUserProfileDto: updatedUser,
      })
      .subscribe({
        next: () => {
          this.handleUpdateSuccess();
        },
        error: (error) => {
          this.handleUpdateError(error);
        },
      });
  }

  private createUpdatedUserDto(): UpdateUserProfileDto {
    return {
      title: this.userFormTitle?.value || undefined,
      firstName: this.userFormFirstName?.value || undefined,
      lastName: this.userFormLastName?.value || undefined,
      email: this.userFormEmail?.value || undefined,
      companyName: this.userFormCompanyName?.value || undefined,
      businessUnit: this.userFormBusinessUnit?.value || undefined,
      country: this.userFormCountry?.value || undefined,
      password: this.userFormPassword?.value || undefined,
    };
  }

  private handleUpdateSuccess() {
    this.userForm.enable();
    this.authStore.setIsLoading(false);
    this.submitted = false;
    this.showAlert = true;
    this.emailTakenError$$.next(false);
    this.updateSuccess$$.next(true);
    this.authStore.refresh().subscribe();
    this.cdr.detectChanges();
    setTimeout(() => {
      this.showAlert = false;
      this.cdr.detectChanges();
    }, EditProfileComponent.ALERT_DISPLAY_TIME);
  }

  private handleUpdateError(error: HttpErrorResponse) {
    this.submitted = false;
    this.userForm.enable();
    this.authStore.setIsLoading(false);
    this.showAlert = true;
    this.updateSuccess$$.next(false);
    console.error('Error while updating profile: ', error.error?.message);
    if (this.isEmailTakenError(error)) {
      this.genericError$$.next(false);
      this.emailTakenError$$.next(true);
      setTimeout(() => {
        this.showAlert = false;
        this.cdr.detectChanges();
      }, EditProfileComponent.ALERT_DISPLAY_TIME);
    } else {
      this.emailTakenError$$.next(false);
      this.genericError$$.next(true);
    }
  }

  private isEmailTakenError(error: HttpErrorResponse): boolean {
    //TODO Remove this hardcoded validation error check for existing email
    return (
      error.error?.message.includes('email') &&
      error.error?.message.includes('already exists')
    );
  }
}
